/**
 * Historical Runs Table Component for Email Ad Validation
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Link,
  Box,
  Collapse
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { emailAdValidationStyles } from '../styles';
import { formatDateToDDMMYY } from '../../../constants/utils';
import ValidationResultsAccordion from './ValidationResultsAccordion.jsx';

const styles = emailAdValidationStyles;

const tableHeaders = [
  { id: 'expand', label: '', sortable: false, width: '50px' },
  { id: 'runName', label: 'Run Name', sortable: true },
  { id: 'submitDate', label: 'Submit Date', sortable: true },
  { id: 'fromEmail', label: 'From Email', sortable: false },
  { id: 'toEmail', label: 'To Email', sortable: false },
  { id: 'subject', label: 'Subject', sortable: false },
  { id: 'dateRange', label: 'Date Range', sortable: true },
  { id: 'emailType', label: 'Email Type', sortable: true },
  { id: 'offerMatrix', label: 'Offer Matrix', sortable: false },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'resultSheet', label: 'Result Sheet', sortable: false },
  { id: 'actions', label: 'Actions', sortable: false },
];

const ValidationRunTable = ({
  historicalRuns = [],
  onAction = () => {},
  totalCount = 0,
  page = 0,
  onPageChange = () => {},
  loading = false,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [expandedRows, setExpandedRows] = useState(new Set())

  // Debug logging to verify data flow
  console.log('📊 ValidationRunTable received data:', {
    count: historicalRuns.length,
    data: historicalRuns,
  });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleAction = (action, run) => {
    onAction(action, run);
  };

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage);
  };

  const toggleRowExpansion = (runId) => {
    const newExpandedRows = new Set(expandedRows)
    if (newExpandedRows.has(runId)) {
      newExpandedRows.delete(runId)
    } else {
      newExpandedRows.add(runId)
    }
    setExpandedRows(newExpandedRows)
  }

  const getStatusChip = status => {
    const statusStyles = {
      COMPLETED: styles.completedChip,
      IN_PROGRESS: styles.runningChip,
      FAILED: styles.failedChip,
    };

    const statusLabels = {
      COMPLETED: 'COMPLETED',
      IN_PROGRESS: 'IN PROGRESS',
      FAILED: 'FAILED',
    };

    return (
      <Chip
        label={statusLabels[status] || status}
        style={{
          ...styles.statusChip,
          ...statusStyles[status],
        }}
      />
    );
  };

  const getSortIcon = (columnId) => {
    if (sortConfig.key !== columnId) {
      return null;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUpIcon style={styles.sortIcon} />
    ) : (
      <ArrowDropDownIcon style={styles.sortIcon} />
    );
  };

  return (
    <TableContainer component={Paper} style={styles.tableContainer}>
      <Table>
        <TableHead sx={styles.tableHeader}>
          <TableRow>
            {tableHeaders.map((header) => (
              <TableCell
                key={header.id}
                onClick={header.sortable ? () => handleSort(header.id) : undefined}
                style={{
                  cursor: header.sortable ? 'pointer' : 'default',
                  width: header.width || 'auto'
                }}
              >
                <Box style={styles.headerCell}>
                  {header.label}
                  {header.sortable && getSortIcon(header.id)}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {historicalRuns.map(run => {
            const isExpanded = expandedRows.has(run.id)
            const hasResults = run.results && run.results.length > 0

            return (
              <React.Fragment key={run.id}>
                <TableRow
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#f9f9f9'
                    },
                    '&:hover': {
                      backgroundColor: '#f0f0f0'
                    }
                  }}
                >
                  <TableCell style={styles.tableCell}>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(run.id)}
                      disabled={!hasResults}
                      style={{
                        opacity: hasResults ? 1 : 0.3,
                        cursor: hasResults ? 'pointer' : 'default'
                      }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell style={styles.tableCell}>{run.run_name || '-'}</TableCell>
                  <TableCell style={styles.tableCell}>
                    {run.submitDate ? formatDateToDDMMYY(run.submitDate) : '-'}
                  </TableCell>
                  <TableCell style={styles.tableCell}>{run.email_from}</TableCell>
                  <TableCell style={styles.tableCell}>{run.email_to || '-'}</TableCell>
                  <TableCell style={styles.tableCell}>{run.subject || '-'}</TableCell>
                  <TableCell style={styles.tableCell}>
                    {run.date_from} To {run.date_to ? run.date_to : 'Present'}
                  </TableCell>
                  <TableCell style={styles.tableCell}>{run.email_type}</TableCell>
                  <TableCell style={styles.tableCell}>
                    {run.offer_matrix_url ? (
                      <Link
                        href={
                          run.offer_matrix_url.startsWith('http')
                            ? run.offer_matrix_url
                            : `https://${run.offer_matrix_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.linkButton}
                      >
                        View Matrix
                      </Link>
                    ) : (
                      <span style={{ color: '#999' }}>No Matrix</span>
                    )}
                  </TableCell>
                  <TableCell style={styles.tableCell}>{getStatusChip(run.status)}</TableCell>
                  <TableCell style={styles.tableCell}>
                    {run.result_sheet_url ? (
                      <Link
                        href={
                          run.result_sheet_url.startsWith('http')
                            ? run.result_sheet_url
                            : `https://${run.result_sheet_url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.linkButton}
                      >
                        View Results
                      </Link>
                    ) : (
                      <span style={{ color: '#999' }}>No Result</span>
                    )}
                  </TableCell>
                  <TableCell style={styles.tableCell}>
                    <Box style={{ display: 'flex', gap: '8px' }}>
                      <Tooltip
                        title={
                          run.status === 'IN_PROGRESS'
                            ? 'Cannot rerun while in progress'
                            : 'Rerun Validation'
                        }
                      >
                        <span>
                          <IconButton
                            style={{
                              ...styles.actionButton,
                              opacity: run.status === 'IN_PROGRESS' ? 0.5 : 1,
                              cursor: run.status === 'IN_PROGRESS' ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => handleAction('rerun', run)}
                            disabled={run.status === 'IN_PROGRESS'}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          run.status === 'IN_PROGRESS'
                            ? 'Cannot delete while in progress'
                            : 'Delete Validation Run'
                        }
                      >
                        <span>
                          <IconButton
                            style={{
                              ...styles.actionButton,
                              opacity: run.status === 'IN_PROGRESS' ? 0.5 : 1,
                              cursor: run.status === 'IN_PROGRESS' ? 'not-allowed' : 'pointer'
                            }}
                            onClick={() => handleAction('delete', run)}
                            disabled={run.status === 'IN_PROGRESS'}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Expandable Results Row */}
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <ValidationResultsAccordion results={run.results} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={10}
        rowsPerPageOptions={[10]}
        showFirstButton
        showLastButton
      />
    </TableContainer>
  );
};

ValidationRunTable.propTypes = {
  historicalRuns: PropTypes.array.isRequired,
  onAction: PropTypes.func,
  totalCount: PropTypes.number,
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  loading: PropTypes.bool,
};

export default ValidationRunTable;
